/**
 * React entry point for the Agentic Loan Audit SPA.
 *
 * On boot:
 *   1. Initialize MSAL before rendering anything that could use it
 *   2. Set up the TanStack Query client
 *   3. Mount the app inside the BrowserRouter
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import App from './App';
import { initMsal } from './auth/msal';
import { ApiClientError } from './api/client';
import { ErrorBoundary } from './acc-ui/components/ErrorBoundary';
import { toast, Toaster } from './acc-ui/primitives/Toast';
// Lato — Axis Finance / Axis Bank's brand typeface (body + headings).
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/lato/900.css';
import '@fontsource-variable/jetbrains-mono'; // self-hosted code/identifiers
import './acc-ui/styles/globals.css';

const queryClient = new QueryClient({
  // Global fallback: every mutation that throws surfaces a toast. Individual
  // mutations may still add their own onError for inline UI; this guarantees
  // no failure is silent (the previous default). Does not alter success paths.
  mutationCache: new MutationCache({
    onError: (error) => {
      const msg =
        error instanceof ApiClientError || error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      toast.error(msg);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Don't retry 4xx responses (client errors). Retry transient 5xx up to 2x.
        const status = (error as { httpStatus?: number }).httpStatus;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

async function bootstrap() {
  try {
    await initMsal();
  } catch (err) {
    // MSAL init failures are non-fatal for the login page render itself,
    // but they indicate misconfiguration that the user should know about.
    // eslint-disable-next-line no-console
    console.error('MSAL failed to initialize', err);
  }

  const root = document.getElementById('root');
  if (!root) throw new Error('#root not found');

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <App />
          </HashRouter>
          <Toaster />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

void bootstrap();
